import { useState } from 'react';
import { Container, Title, Table, Button, Group, Modal, Stack, Rating, Textarea, Checkbox, Text, Badge, Paper, SimpleGrid } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconStar } from '@tabler/icons-react';
import { useDeliveryFeedbackHistory, useSubmitDeliveryFeedback, useQualityTrends } from '@/hooks/usePortalApi';

export default function QualityFeedback() {
  const { data: historyData } = useDeliveryFeedbackHistory();
  const { data: trendsData } = useQualityTrends();
  const submitMut = useSubmitDeliveryFeedback();
  const [opened, { open, close }] = useDisclosure(false);
  const [orderId, setOrderId] = useState<number | ''>('');
  const [rating, setRating] = useState(5);
  const [comments, setComments] = useState('');
  const [tempIssue, setTempIssue] = useState(false);
  const [packIssue, setPackIssue] = useState(false);
  const [freshIssue, setFreshIssue] = useState(false);

  const feedback = historyData?.feedback ?? [];
  const trends = trendsData;

  const handleSubmit = () => {
    if (!orderId) return;
    submitMut.mutate({
      order_id: Number(orderId), overall_rating: rating, comments: comments || undefined,
      issues: { temperature: tempIssue, packaging: packIssue, freshness: freshIssue },
    }, {
      onSuccess: () => { close(); notifications.show({ title: 'Submitted', message: 'Feedback recorded', color: 'green' }); },
    });
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Quality Feedback</Title>
        <Button leftSection={<IconStar size={16} />} onClick={open}>Rate Delivery</Button>
      </Group>

      {trends && (
        <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
          <Paper p="md" withBorder>
            <Text size="sm" c="dimmed">Overall Average</Text>
            <Text size="xl" fw={700}>{trends.overall_avg} / 5</Text>
          </Paper>
          <Paper p="md" withBorder>
            <Text size="sm" c="dimmed">Temperature Issues</Text>
            <Text size="xl" fw={700}>{trends.issue_counts.temperature}</Text>
          </Paper>
          <Paper p="md" withBorder>
            <Text size="sm" c="dimmed">Freshness Issues</Text>
            <Text size="xl" fw={700}>{trends.issue_counts.freshness}</Text>
          </Paper>
        </SimpleGrid>
      )}

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Date</Table.Th>
            <Table.Th>Order</Table.Th>
            <Table.Th>Rating</Table.Th>
            <Table.Th>Issues</Table.Th>
            <Table.Th>Status</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {feedback.map((f) => (
            <Table.Tr key={f.id}>
              <Table.Td>{f.delivery_date}</Table.Td>
              <Table.Td>#{f.order_id}</Table.Td>
              <Table.Td><Rating value={f.overall_rating} readOnly size="xs" /></Table.Td>
              <Table.Td>
                <Group gap={4}>
                  {f.temperature_issue && <Badge size="xs" color="red">Temp</Badge>}
                  {f.packaging_issue && <Badge size="xs" color="orange">Pack</Badge>}
                  {f.freshness_issue && <Badge size="xs" color="yellow">Fresh</Badge>}
                </Group>
              </Table.Td>
              <Table.Td><Badge>{f.status}</Badge></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={opened} onClose={close} title="Rate Delivery">
        <Stack>
          <Text>Order ID</Text>
          <input type="number" value={orderId} onChange={(e) => setOrderId(Number(e.target.value) || '')} style={{ padding: 8, border: '1px solid #ccc', borderRadius: 4 }} />
          <Text>Overall Rating</Text>
          <Rating value={rating} onChange={setRating} size="lg" />
          <Textarea label="Comments" value={comments} onChange={(e) => setComments(e.currentTarget.value)} />
          <Checkbox label="Temperature issue" checked={tempIssue} onChange={(e) => setTempIssue(e.currentTarget.checked)} />
          <Checkbox label="Packaging issue" checked={packIssue} onChange={(e) => setPackIssue(e.currentTarget.checked)} />
          <Checkbox label="Freshness issue" checked={freshIssue} onChange={(e) => setFreshIssue(e.currentTarget.checked)} />
          <Button onClick={handleSubmit} loading={submitMut.isPending}>Submit Feedback</Button>
        </Stack>
      </Modal>
    </Container>
  );
}
